import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronsRight,
  ClipboardPaste,
  Download,
  Eye,
  Film,
  Frame,
  Grid3x3,
  Images,
  Info,
  List,
  Music,
  Palette,
  Play,
  PlayCircle,
  Plus,
  Rows4,
  Sparkles,
  Timer,
  Type,
  Wand2,
  X,
  Zap
} from 'lucide-react';

/* ============================================================================
   BLACKBOXcr — Tutorial
   ----------------------------------------------------------------------------
   Rewritten to feel light. The old version was a 6-step, 18-minute, sidebar-
   and-progress-bar course. This one is a short guided demo built around the
   two things that actually make the app feel effortless:

     1. Batch Add  — paste a list, glance at it, commit. That's the whole
                     ingestion story, and it's shown running, not described.
     2. Logos      — the Logo Board (rows) and Full Width Title.

   Everything else (type, sequence, timing, export) is compressed into one
   light chapter, because none of it blocks a user from finishing a sequence.

   The Batch Add demo below re-implements getBatchRowTypes() from App.js so
   the badges shown here are literally what the app will produce. If that
   parser changes in App.js, mirror it in classifyRows() here.
   ========================================================================== */

// ---------------------------------------------------------------------------
// Sample paste — mirrors BATCH_SAMPLE_SCROLL in App.js
// ---------------------------------------------------------------------------
const SAMPLE_LINES = [
  'Directed by\tAVA GREEN',
  'Written by\tELIJAH CARTER',
  'Produced by\tSOFIA NADEEM',
  'CAST',
  'MARCUS\tJASON MITCHELL',
  'DELIA\tTAYE DIGGS',
  'THE WARDEN\tRON PERLMAN',
  'Director of Photography\tLEO YAMAMOTO',
  'Edited by\tISAAC BAUTISTA',
  'Stunts',
  '\tRAY OKONKWO',
  '\tMIA HALVORSEN',
  '\tDEVON PRICE',
  'Music by\tNOAH ROSSI'
];

const BADGE_META = {
  dual: { label: 'Role + Name', cls: 'bg-blue-500/15 text-blue-300 ring-blue-500/25' },
  single: { label: 'Section Divider', cls: 'bg-purple-500/15 text-purple-300 ring-purple-500/25' },
  title: { label: 'Header', cls: 'bg-purple-500/15 text-purple-300 ring-purple-500/25' },
  'group-header': { label: 'Multiname Header', cls: 'bg-amber-500/15 text-amber-300 ring-amber-500/25' },
  'group-member': { label: 'Grouped Below', cls: 'bg-zinc-800 text-zinc-400 ring-white/10' },
  empty: { label: 'Empty', cls: 'bg-zinc-900 text-zinc-600 ring-white/5' }
};

// Mirror of parseBatchPasteToRows() in App.js
const parseRows = (lines) =>
  lines
    .filter((l) => l.trim().length > 0)
    .map((line, idx) => {
      let cols = line.split('\t');
      if (cols.length < 2) cols = line.split(/\s{2,}/);
      return { id: idx, role: (cols[0] || '').trim(), name: (cols[1] || '').trim() };
    });

// Mirror of getBatchRowTypes() in App.js (auto-detect path only — no manual overrides here)
const classifyRows = (rows) => {
  const types = new Array(rows.length).fill('empty');
  for (let i = 0; i < rows.length; i++) {
    const role = (rows[i].role || '').trim();
    const name = (rows[i].name || '').trim();
    if (!role && !name) { types[i] = 'empty'; continue; }
    if (role && name) { types[i] = 'dual'; continue; }
    if (role && !name) {
      let look = i + 1;
      let sawName = false;
      while (look < rows.length && !(rows[look].role || '').trim() && (rows[look].name || '').trim()) {
        sawName = true;
        types[look] = 'group-member';
        look++;
      }
      types[i] = sawName ? 'group-header' : 'title';
      i = look - 1;
      continue;
    }
    if (!role && name) types[i] = 'single';
  }
  return types;
};

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------
const Pill = ({ children, tone = 'purple' }) => {
  const tones = {
    purple: 'border-purple-500/30 bg-purple-500/10 text-purple-300',
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
    green: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
    zinc: 'border-white/10 bg-white/5 text-zinc-400'
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Beat = ({ n, title, children }) => (
  <div className="flex gap-4">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[13px] font-bold text-purple-300">
      {n}
    </div>
    <div className="pt-1">
      <h4 className="text-[15px] font-semibold text-white">{title}</h4>
      <p className="mt-1 text-[13.5px] leading-relaxed text-zinc-400">{children}</p>
    </div>
  </div>
);

// ===========================================================================
// BATCH ADD — the centerpiece. Plays the real thing, line by line.
// ===========================================================================
const BatchAddDemo = () => {
  const [revealed, setRevealed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [committed, setCommitted] = useState(false);
  const timerRef = useRef(null);

  const play = useCallback(() => {
    clearInterval(timerRef.current);
    setCommitted(false);
    setRevealed(0);
    setPlaying(true);
  }, []);

  // Autoplay once on mount, then hand control to the user.
  useEffect(() => {
    const t = setTimeout(play, 500);
    return () => clearTimeout(t);
  }, [play]);

  useEffect(() => {
    if (!playing) return undefined;
    timerRef.current = setInterval(() => {
      setRevealed((r) => {
        if (r >= SAMPLE_LINES.length) {
          clearInterval(timerRef.current);
          setPlaying(false);
          return r;
        }
        return r + 1;
      });
    }, 130);
    return () => clearInterval(timerRef.current);
  }, [playing]);

  const visibleLines = SAMPLE_LINES.slice(0, revealed);
  const rows = useMemo(() => parseRows(visibleLines), [revealed]); // eslint-disable-line react-hooks/exhaustive-deps
  const types = useMemo(() => classifyRows(rows), [rows]);
  const done = revealed >= SAMPLE_LINES.length && !playing;

  const count = (t) => types.filter((x) => x === t).length;
  const paired = count('dual');
  const sections = count('single') + count('title');
  const groups = count('group-header');

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 shadow-[0_40px_120px_-40px_rgba(88,28,235,0.45)]">
      {/* chrome */}
      <div className="flex items-center justify-between gap-4 border-b border-white/5 bg-gradient-to-b from-purple-950/30 to-transparent px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-purple-500/30 bg-purple-500/10 text-purple-300">
            <Rows4 size={15} />
          </div>
          <div>
            <p className="text-[13.5px] font-semibold leading-none text-white">Paste Your Credit List</p>
            <p className="mt-1 text-[10.5px] uppercase tracking-[0.14em] text-purple-400/80">Fastest way in</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={play}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-400 transition-colors hover:border-purple-500/40 hover:bg-purple-500/10 hover:text-purple-200"
          >
            <Play size={11} /> {done || committed ? 'Replay' : 'Playing'}
          </button>
          <X size={15} className="text-zinc-700" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2">
        {/* LEFT — the paste */}
        <div className="border-white/5 p-4 lg:border-r">
          <p className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600">
            <ClipboardPaste size={12} /> Your list
          </p>
          <div className="h-[260px] overflow-hidden rounded-xl border border-white/10 bg-black/60 p-3 font-mono text-[12px] leading-[1.75]">
            {visibleLines.length === 0 && (
              <span className="text-zinc-700">Paste anything here…</span>
            )}
            {visibleLines.map((line, i) => {
              const [a, b] = line.split('\t');
              return (
                <div key={i} className="tut-line whitespace-pre text-zinc-300">
                  <span className="text-zinc-400">{a}</span>
                  {b !== undefined && <span className="text-zinc-700">{'\t'}</span>}
                  {b !== undefined && <span className="text-purple-300">{b}</span>}
                </div>
              );
            })}
            {playing && <span className="tut-caret inline-block h-[14px] w-[7px] translate-y-[2px] bg-purple-400" />}
          </div>
          <p className="mt-2 text-center text-[11px] text-zinc-600">
            Spreadsheet, script, email, notes — or drop a .txt / .csv right here.
          </p>
        </div>

        {/* RIGHT — live preview */}
        <div className="bg-black/40 p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600">Preview</span>
            {paired > 0 && <span className="rounded-md bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-300">{paired} paired</span>}
            {sections > 0 && <span className="rounded-md bg-purple-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-purple-300">{sections} header</span>}
            {groups > 0 && <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-300">{groups} group</span>}
          </div>

          <div className="custom-scrollbar h-[260px] overflow-y-auto rounded-xl border border-white/10 bg-zinc-950/80">
            {rows.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                <Wand2 size={18} className="text-zinc-700" />
                <p className="max-w-[16rem] text-[12px] leading-relaxed text-zinc-600">
                  Every row lands here — typed, editable, and yours to correct before a single credit is created.
                </p>
              </div>
            ) : (
              <table className="w-full">
                <tbody>
                  {rows.map((row, i) => {
                    const meta = BADGE_META[types[i]] || BADGE_META.empty;
                    const member = types[i] === 'group-member';
                    return (
                      <tr key={row.id} className="tut-row border-b border-white/5 last:border-0">
                        <td className="w-[38%] px-3 py-[7px]">
                          <span className={`block truncate text-[12.5px] text-zinc-200 ${member ? 'pl-4' : ''}`}>
                            {row.role || <span className="text-zinc-700">—</span>}
                          </span>
                        </td>
                        <td className="w-[34%] px-1 py-[7px]">
                          <span className="block truncate text-[12.5px] text-zinc-300">{row.name}</span>
                        </td>
                        <td className="px-2 py-[7px] text-right">
                          <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ring-1 ${meta.cls}`}>
                            {meta.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p className="mt-2 text-center text-[11px] text-zinc-600">
            Auto-detected. Wrong on a row? Change it in that row&rsquo;s Type dropdown.
          </p>
        </div>
      </div>

      {/* commit */}
      <div className="flex flex-col items-center gap-3 border-t border-white/10 bg-black/60 px-5 py-4 sm:flex-row sm:justify-between">
        <p className="text-[12px] text-zinc-500">
          Nothing enters your project until you commit.
        </p>
        <button
          onClick={() => setCommitted(true)}
          disabled={!done || committed}
          className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white transition-all ${
            committed
              ? 'bg-emerald-600'
              : done
              ? 'tut-pulse bg-gradient-to-b from-purple-500 to-purple-700 shadow-lg shadow-purple-950/60 hover:from-purple-400'
              : 'cursor-not-allowed bg-zinc-900 text-zinc-600'
          }`}
        >
          {committed ? <Check size={15} /> : <Zap size={15} />}
          {committed ? `${rows.length} credits added` : `Add ${rows.length} Credits`}
        </button>
      </div>
    </div>
  );
};

// ===========================================================================
// LOGOS — Logo Board vs Full Width Title
// ===========================================================================
const SIZE_ORDER = ['xs', 'small', 'medium', 'large', 'xl'];
const SIZE_LABEL = { xs: 'X-Small', small: 'Small', medium: 'Medium', large: 'Large', xl: 'X-Large' };
const SIZE_H = { xs: 16, small: 22, medium: 30, large: 40, xl: 52 };

const LogoChip = ({ label, size, onCycle, tone }) => (
  <button
    onClick={onCycle}
    title={`${label} — ${SIZE_LABEL[size]} (click to resize)`}
    className="group flex flex-col items-center gap-1"
  >
    <div
      className={`flex items-center justify-center rounded-md border px-3 transition-all duration-300 ${tone}`}
      style={{ height: SIZE_H[size], minWidth: SIZE_H[size] * 2.1 }}
    >
      <span className="text-[9px] font-bold uppercase tracking-[0.14em] opacity-80">{label}</span>
    </div>
    <span className="text-[9px] uppercase tracking-wide text-zinc-700 transition-colors group-hover:text-purple-400">
      {SIZE_LABEL[size]}
    </span>
  </button>
);

const LogoDemo = () => {
  const [mode, setMode] = useState('board');
  const [sizes, setSizes] = useState({ a: 'medium', b: 'medium', c: 'medium', d: 'large' });

  const cycle = (k) =>
    setSizes((s) => ({ ...s, [k]: SIZE_ORDER[(SIZE_ORDER.indexOf(s[k]) + 1) % SIZE_ORDER.length] }));

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950">
      {/* mode switch */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-4 py-3">
        <div className="inline-flex gap-1 rounded-xl border border-white/10 bg-black/50 p-1">
          {[
            { v: 'board', icon: Grid3x3, label: 'Logo Board', hint: 'Rows, up to 5 each' },
            { v: 'full', icon: Frame, label: 'Full Width Title', hint: 'One mark, whole frame' }
          ].map((o) => {
            const Icon = o.icon;
            const on = mode === o.v;
            return (
              <button
                key={o.v}
                onClick={() => setMode(o.v)}
                className={`flex items-center gap-2.5 rounded-lg px-3.5 py-2 text-left transition-all ${
                  on ? 'bg-gradient-to-b from-amber-500 to-amber-600 text-black shadow-lg shadow-amber-950/40' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'
                }`}
              >
                <Icon size={15} />
                <span className="leading-tight">
                  <span className="block text-[12.5px] font-semibold">{o.label}</span>
                  <span className={`block text-[9.5px] uppercase tracking-wider ${on ? 'text-black/60' : 'text-zinc-600'}`}>{o.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
        {mode === 'board' && (
          <span className="text-[11px] text-zinc-600">Click any logo to resize it →</span>
        )}
      </div>

      {/* frame preview */}
      <div className="p-5">
        <div className="relative flex aspect-[16/7] items-center justify-center overflow-hidden rounded-xl border border-white/5 bg-black">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.04),transparent_70%)]" />
          {mode === 'board' ? (
            <div className="flex w-[85%] flex-col items-center gap-5">
              <div className="flex items-end justify-center gap-6">
                <LogoChip label="Meridian" size={sizes.a} onCycle={() => cycle('a')} tone="border-amber-400/40 bg-amber-400/10 text-amber-200" />
                <LogoChip label="Northgate" size={sizes.b} onCycle={() => cycle('b')} tone="border-zinc-500/40 bg-white/[0.06] text-zinc-200" />
                <LogoChip label="Ninth Ward" size={sizes.c} onCycle={() => cycle('c')} tone="border-zinc-500/40 bg-white/[0.06] text-zinc-200" />
              </div>
              <div className="h-px w-2/3 bg-white/5" />
              <div className="flex items-end justify-center">
                <LogoChip label="Blacklight Dist." size={sizes.d} onCycle={() => cycle('d')} tone="border-purple-400/40 bg-purple-400/10 text-purple-200" />
              </div>
            </div>
          ) : (
            <div className="tut-fade flex w-[86%] items-center justify-center rounded-md border border-purple-400/30 bg-purple-400/10 py-9">
              <span className="text-[13px] font-bold uppercase tracking-[0.32em] text-purple-200">Blacklight Films</span>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-[13px] leading-relaxed text-zinc-400">
          {mode === 'board' ? (
            <>
              Rows, not a grid. Add as many as the credit needs, up to five logos per row, and size each one on its
              own — a lead studio mark can stand taller than the partners beside it. Drag files straight onto a row,
              drag cards between rows to rearrange.
            </>
          ) : (
            <>
              When one mark should own the frame — a studio card, an &ldquo;in association with,&rdquo; a distributor
              bumper — this is the layout. One logo, full width, nothing else competing with it.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

// ===========================================================================
// TIMING toy — scroll speed you can feel
// ===========================================================================
const ScrollSpeedDemo = () => {
  const [dur, setDur] = useState(9);
  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-950 p-5">
      <div className="relative h-40 overflow-hidden rounded-xl border border-white/5 bg-black">
        <div
          className="tut-roll absolute inset-x-0 space-y-2 text-center"
          style={{ animationDuration: `${dur}s` }}
        >
          {[
            ['Directed by', 'AVA GREEN'],
            ['Written by', 'ELIJAH CARTER'],
            ['Produced by', 'SOFIA NADEEM'],
            ['Director of Photography', 'LEO YAMAMOTO'],
            ['Edited by', 'ISAAC BAUTISTA'],
            ['Music by', 'NOAH ROSSI']
          ].map(([r, n]) => (
            <div key={r} className="flex justify-center gap-4 text-[11px]">
              <span className="w-40 text-right text-zinc-500">{r}</span>
              <span className="w-40 text-left text-zinc-200">{n}</span>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black to-transparent" />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Timer size={14} className="text-purple-400" />
        <input
          type="range"
          min="4"
          max="18"
          step="1"
          value={dur}
          onChange={(e) => setDur(Number(e.target.value))}
          className="tut-range h-1 flex-1 cursor-pointer appearance-none rounded-full bg-zinc-800"
        />
        <span className="w-20 text-right text-[11px] tabular-nums text-zinc-500">{dur}s roll</span>
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-zinc-500">
        Set a speed, or hand us your music file and hit <span className="text-purple-300">Sync Scroll to Music</span> —
        we read the duration and do the math.
      </p>
    </div>
  );
};

// ===========================================================================
// CHAPTERS
// ===========================================================================
const CHAPTERS = [
  { id: 'start', label: 'Start', icon: Sparkles },
  { id: 'paste', label: 'Paste your list', icon: ClipboardPaste },
  { id: 'logos', label: 'Your logos', icon: Images },
  { id: 'look', label: 'Make it yours', icon: Palette },
  { id: 'ship', label: 'Play & ship', icon: PlayCircle },
  { id: 'answers', label: 'Quick answers', icon: Info }
];

const FAQS = [
  {
    q: "I don't have a clean spreadsheet. Does that matter?",
    a: "No. Batch Add takes a rough paste from a script, an email, or notes. A tab — or two spaces — separates Role from Name, and everything else is detected. If a row lands wrong, change it in that row's Type dropdown before you commit."
  },
  {
    q: 'What if my list is in end-credits order and I need it up front?',
    a: 'Hit Reverse Order in the preview. Swap Role / Name is right next to it if your columns came in backwards.'
  },
  {
    q: 'Can I drop credits into the middle of my list?',
    a: 'Yes. In the footer, pick any existing header or group and insert after it. If you point at a multi-name group, you can merge the new names straight into it instead of starting a new section.'
  },
  {
    q: 'Logo Board or Full Width Title?',
    a: 'Logo Board for a block of marks — rows, up to five per row, each logo sized on its own. Full Width Title when one logo should own the whole frame. Nothing in the Logo Board touches your credits until you hit Commit to Credit.'
  },
  {
    q: 'Do I have to style every credit?',
    a: "No. Set the project font once in Typography and everything follows it. Click an individual credit only when you want it to break the rule."
  },
  {
    q: 'What can I export?',
    a: 'H.264/H.265 up to 1080p on Indie; 4K plus ProRes 422 and 4444 with alpha on Pro. Full sequence, scroll only, or cards only — plus XML into Premiere, Resolve, or FCPX.'
  }
];

// ===========================================================================
const TutorialPage = ({ onExit, onComplete }) => {
  const [chapter, setChapter] = useState('start');
  const [openFAQ, setOpenFAQ] = useState(null);
  const idx = CHAPTERS.findIndex((c) => c.id === chapter);

  const go = useCallback((id) => {
    setChapter(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const next = useCallback(() => {
    if (idx < CHAPTERS.length - 1) go(CHAPTERS[idx + 1].id);
  }, [idx, go]);

  const prev = useCallback(() => {
    if (idx > 0) go(CHAPTERS[idx - 1].id);
  }, [idx, go]);

  // Arrow keys move between chapters — the whole thing should feel flippable.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  return (
    <div className="min-h-screen bg-black font-poppins text-zinc-100">
      <style>{`
        @keyframes tutIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        @keyframes tutLine { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: none; } }
        @keyframes tutRow { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
        @keyframes tutCaret { 0%,49% { opacity: 1; } 50%,100% { opacity: 0; } }
        @keyframes tutPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(168,85,247,0.45); } 50% { box-shadow: 0 0 0 10px rgba(168,85,247,0); } }
        @keyframes tutRoll { from { transform: translateY(160px); } to { transform: translateY(-100%); } }
        @keyframes tutFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .tut-in { animation: tutIn .45s cubic-bezier(.2,.7,.3,1) both; }
        .tut-line { animation: tutLine .22s ease-out both; }
        .tut-row { animation: tutRow .26s ease-out both; }
        .tut-caret { animation: tutCaret 1s steps(1) infinite; }
        .tut-pulse { animation: tutPulse 1.8s ease-out infinite; }
        .tut-roll { animation: tutRoll linear infinite; }
        .tut-fade { animation: tutIn .4s ease-out both; }
        .tut-float { animation: tutFloat 4s ease-in-out infinite; }
        .tut-range::-webkit-slider-thumb {
          -webkit-appearance: none; height: 14px; width: 14px; border-radius: 9999px;
          background: rgb(168 85 247); cursor: pointer; box-shadow: 0 0 0 4px rgba(168,85,247,.18);
        }
        .tut-range::-moz-range-thumb {
          height: 14px; width: 14px; border: 0; border-radius: 9999px; background: rgb(168 85 247); cursor: pointer;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgb(63 63 70); border-radius: 3px; }
      `}</style>

      {/* ---------------- top bar ---------------- */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3">
          <button
            onClick={onExit}
            className="flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-zinc-500 transition-colors hover:text-zinc-200"
          >
            <ArrowLeft size={14} /> <span className="hidden sm:inline">Projects</span>
          </button>

          <div className="mx-1 h-4 w-px bg-white/10" />

          <h1 className="shrink-0 text-[15px] font-thin tracking-wide">
            <span className="text-white">BLACKBOX</span>
            <span className="text-yellow-400">cr</span>
          </h1>

          {/* chapter dots */}
          <nav className="custom-scrollbar ml-auto flex items-center gap-1 overflow-x-auto">
            {CHAPTERS.map((c, i) => {
              const on = c.id === chapter;
              return (
                <button
                  key={c.id}
                  onClick={() => go(c.id)}
                  title={c.label}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-medium transition-all ${
                    on
                      ? 'bg-white/[0.07] text-white'
                      : i < idx
                      ? 'text-zinc-600 hover:text-zinc-300'
                      : 'text-zinc-600 hover:text-zinc-300'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full transition-colors ${on ? 'bg-purple-400' : i < idx ? 'bg-purple-900' : 'bg-zinc-800'}`} />
                  <span className={on ? 'inline' : 'hidden lg:inline'}>{c.label}</span>
                </button>
              );
            })}
          </nav>

          <button
            onClick={onComplete}
            className="ml-2 hidden shrink-0 rounded-lg border border-white/10 px-3 py-1.5 text-[12px] font-medium text-zinc-400 transition-colors hover:border-white/20 hover:text-white sm:block"
          >
            Skip
          </button>
        </div>
      </header>

      <main key={chapter} className="tut-in mx-auto max-w-5xl px-5 pb-24 pt-12">
        {/* ================= START ================= */}
        {chapter === 'start' && (
          <div className="space-y-14">
            <div className="text-center">
              <Pill>60 seconds</Pill>
              <h2 className="mx-auto mt-6 max-w-3xl text-[44px] font-semibold leading-[1.08] tracking-tight text-white sm:text-[56px]">
                Three things,
                <br />
                and your credits are done.
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-zinc-400">
                No template to download. No panels to hunt for. Paste a list, drop in your logos, hit play.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { icon: ClipboardPaste, title: 'Paste', line: 'Your whole cast and crew list, in one go.', tone: 'purple' },
                { icon: Images, title: 'Drop', line: 'Logos onto a board. Arrange, resize, done.', tone: 'amber' },
                { icon: PlayCircle, title: 'Play', line: 'Watch it roll. Export when it looks right.', tone: 'green' }
              ].map((s, i) => {
                const Icon = s.icon;
                const tone = {
                  purple: 'from-purple-600/20 text-purple-300 border-purple-500/25',
                  amber: 'from-amber-500/20 text-amber-300 border-amber-500/25',
                  green: 'from-emerald-500/20 text-emerald-300 border-emerald-500/25'
                }[s.tone];
                return (
                  <div
                    key={s.title}
                    className="group rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-transparent p-7 transition-colors hover:border-white/15"
                  >
                    <div
                      className={`tut-float mb-5 flex h-12 w-12 items-center justify-center rounded-xl border bg-gradient-to-b to-transparent ${tone}`}
                      style={{ animationDelay: `${i * 0.5}s` }}
                    >
                      <Icon size={22} />
                    </div>
                    <h3 className="text-[20px] font-semibold text-white">{s.title}</h3>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-zinc-500">{s.line}</p>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center gap-4">
              <button
                onClick={() => go('paste')}
                className="group inline-flex items-center gap-2.5 rounded-xl bg-white px-7 py-3.5 text-[15px] font-semibold text-black transition-transform hover:scale-[1.02]"
              >
                Show me the fast way
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button onClick={onComplete} className="text-[13px] text-zinc-600 transition-colors hover:text-zinc-300">
                I&rsquo;d rather just poke around
              </button>
            </div>
          </div>
        )}

        {/* ================= PASTE ================= */}
        {chapter === 'paste' && (
          <div className="space-y-10">
            <div className="text-center">
              <Pill>Step one of three</Pill>
              <h2 className="mx-auto mt-5 max-w-2xl text-[38px] font-semibold leading-[1.1] tracking-tight text-white sm:text-[46px]">
                Paste the list. That&rsquo;s the import.
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-zinc-400">
                Copy from a spreadsheet, a script, an email — anywhere. Sections, multi-name groups, and role/name pairs
                sort themselves out. Watch:
              </p>
            </div>

            <BatchAddDemo />

            <div className="grid gap-8 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-8 sm:grid-cols-3">
              <Beat n="1" title="Paste">
                A tab — or two spaces — separates Role from Name. That&rsquo;s the only rule, and a spreadsheet copy
                already does it for you.
              </Beat>
              <Beat n="2" title="Glance">
                Every row is labelled and editable right there. Fix a typo, change a row&rsquo;s type, add or delete a
                line.
              </Beat>
              <Beat n="3" title="Commit">
                Choose the end of the list or drop it after any existing section. Nothing enters your project before
                you say so.
              </Beat>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { icon: Sparkles, t: 'Try a sample', d: 'One click loads a real list so you can see it work before you have your own.' },
                { icon: List, t: 'Title Cards too', d: 'Flip to Title Cards and each line becomes its own card, stacked as it will render.' },
                { icon: Wand2, t: 'Ask the Copilot', d: "Not sure how yours should be laid out? It's one click from inside the panel." }
              ].map((x) => {
                const Icon = x.icon;
                return (
                  <div key={x.t} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-5">
                    <Icon size={17} className="mb-3 text-purple-400" />
                    <h4 className="text-[13.5px] font-semibold text-white">{x.t}</h4>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-500">{x.d}</p>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-[12.5px] text-zinc-600">
              Prefer a file? <span className="text-zinc-400">Import CSV</span> is still there — and you can drag a
              .csv or .txt straight onto the paste pane.
            </p>
          </div>
        )}

        {/* ================= LOGOS ================= */}
        {chapter === 'logos' && (
          <div className="space-y-10">
            <div className="text-center">
              <Pill tone="amber">Step two of three</Pill>
              <h2 className="mx-auto mt-5 max-w-2xl text-[38px] font-semibold leading-[1.1] tracking-tight text-white sm:text-[46px]">
                Logos, arranged the way they actually sit.
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-zinc-400">
                Two layouts cover every logo moment in a film. Try them both — click the tabs, click the logos.
              </p>
            </div>

            <LogoDemo />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: Plus, t: 'Drag files in', d: 'Drop images anywhere on the board, or onto one row. Drop onto an existing logo to replace it.' },
                { icon: Grid3x3, t: 'Rearrange freely', d: 'Drag logo cards between rows, nudge left and right, move whole rows up or down.' },
                { icon: Images, t: 'Reuse from library', d: 'Every logo you upload is saved. Pick it again next credit — or next project.' },
                { icon: Check, t: 'Commit when ready', d: 'The board is a workspace. Your credit only changes when you hit Commit to Credit.' }
              ].map((x) => {
                const Icon = x.icon;
                return (
                  <div key={x.t} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-5">
                    <Icon size={17} className="mb-3 text-amber-400" />
                    <h4 className="text-[13.5px] font-semibold text-white">{x.t}</h4>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-500">{x.d}</p>
                  </div>
                );
              })}
            </div>

            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-6 py-5">
              <p className="text-[13.5px] leading-relaxed text-zinc-400">
                <span className="font-semibold text-white">Board-wide dials, too.</span> Block Width, Row/Logo Spacing,
                and an Overall Size that scales every logo on the board at once — for when the whole block just needs to
                sit a little bigger in frame.
              </p>
            </div>
          </div>
        )}

        {/* ================= LOOK ================= */}
        {chapter === 'look' && (
          <div className="space-y-10">
            <div className="text-center">
              <Pill>Step three of three</Pill>
              <h2 className="mx-auto mt-5 max-w-2xl text-[38px] font-semibold leading-[1.1] tracking-tight text-white sm:text-[46px]">
                Set it once. It follows everywhere.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-[16px] leading-relaxed text-zinc-400">
                Type, order, and speed are three menus in the command bar. Most projects touch each of them once.
              </p>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6">
                <div className="mb-4 flex items-center gap-2.5">
                  <Type size={17} className="text-purple-400" />
                  <h3 className="text-[15px] font-semibold text-white">Typography</h3>
                </div>
                <div className="space-y-2">
                  {[
                    { n: 'Helvetica', c: 'font-sans', h: 'Classic film' },
                    { n: 'EB Garamond', c: 'font-serif', h: 'Elegant' },
                    { n: 'Your own upload', c: '', h: 'Custom font' }
                  ].map((f) => (
                    <div key={f.n} className="flex items-center justify-between rounded-lg border border-white/5 bg-black/40 px-4 py-3">
                      <span className={`text-[14px] text-white ${f.c}`}>{f.n}</span>
                      <span className="text-[10px] uppercase tracking-wider text-zinc-600">{f.h}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[12.5px] leading-relaxed text-zinc-500">
                  One font, size, weight, tracking, and colour for the whole project. Click any single credit to break
                  the rule where you want emphasis.
                </p>
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6">
                <div className="mb-4 flex items-center gap-2.5">
                  <Rows4 size={17} className="text-purple-400" />
                  <h3 className="text-[15px] font-semibold text-white">Sequence</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { t: 'Cards → Scroll', d: 'Traditional film' },
                    { t: 'Scroll → Cards', d: 'TV series' },
                    { t: 'Scroll only', d: 'End roll' },
                    { t: 'Cards only', d: 'Short form' }
                  ].map((o) => (
                    <div key={o.t} className="rounded-lg border border-white/5 bg-black/40 px-3 py-3.5 text-center transition-colors hover:border-purple-500/40">
                      <p className="text-[12.5px] font-semibold text-white">{o.t}</p>
                      <p className="mt-0.5 text-[10px] uppercase tracking-wider text-zinc-600">{o.d}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[12.5px] leading-relaxed text-zinc-500">
                  Plus intermission between cards and fade in/out — the two knobs anyone ever reaches for.
                </p>
              </div>
            </div>

            <div>
              <div className="mb-4 flex items-center gap-2.5">
                <Music size={17} className="text-purple-400" />
                <h3 className="text-[15px] font-semibold text-white">Timing — drag it and see</h3>
              </div>
              <ScrollSpeedDemo />
            </div>
          </div>
        )}

        {/* ================= SHIP ================= */}
        {chapter === 'ship' && (
          <div className="space-y-10">
            <div className="text-center">
              <Pill tone="green">The easy part</Pill>
              <h2 className="mx-auto mt-5 max-w-2xl text-[38px] font-semibold leading-[1.1] tracking-tight text-white sm:text-[46px]">
                Play it. Then hand it to the edit.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-[16px] leading-relaxed text-zinc-400">
                Preview is live and free — hit Play as often as you like, at any point, with your music.
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-3">
              {[
                { n: 'Draft', specs: ['720p', '30 FPS', 'H.264'], t: '5–10 min', bar: 'from-emerald-500 to-teal-500' },
                { n: 'Standard', specs: ['1080p / 4K', '60 FPS', 'H.264 / H.265'], t: '10–25 min', bar: 'from-blue-500 to-cyan-500' },
                { n: 'Cinematic', specs: ['4K', '120 FPS', 'ProRes / Alpha'], t: '45–60 min', bar: 'from-purple-500 to-pink-500' }
              ].map((tier) => (
                <div key={tier.n} className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6">
                  <div className={`mb-4 h-1 rounded-full bg-gradient-to-r ${tier.bar}`} />
                  <h4 className="text-[19px] font-semibold text-white">{tier.n}</h4>
                  <div className="mt-3 space-y-1.5">
                    {tier.specs.map((s) => (
                      <p key={s} className="flex items-center gap-2 text-[12.5px] text-zinc-400">
                        <Check size={13} className="text-emerald-400" /> {s}
                      </p>
                    ))}
                  </div>
                  <p className="mt-4 border-t border-white/5 pt-3 text-[11px] text-zinc-600">~{tier.t}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { icon: Eye, t: 'Preview anytime', d: 'Broadcast-quality, in the browser, synced to your track.' },
                { icon: Download, t: 'Export what you need', d: 'Full sequence, scroll only, or cards as separate clips.' },
                { icon: Film, t: 'Straight into the NLE', d: 'XML for Premiere, Resolve, and FCPX. ProRes 4444 with alpha on Pro.' }
              ].map((x) => {
                const Icon = x.icon;
                return (
                  <div key={x.t} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-5">
                    <Icon size={17} className="mb-3 text-emerald-400" />
                    <h4 className="text-[13.5px] font-semibold text-white">{x.t}</h4>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-500">{x.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ================= ANSWERS ================= */}
        {chapter === 'answers' && (
          <div className="space-y-10">
            <div className="text-center">
              <Pill tone="zinc">Just in case</Pill>
              <h2 className="mx-auto mt-5 max-w-2xl text-[38px] font-semibold leading-[1.1] tracking-tight text-white sm:text-[46px]">
                Quick answers.
              </h2>
            </div>

            <div className="space-y-2">
              {FAQS.map((f, i) => (
                <div key={i} className="overflow-hidden rounded-xl border border-white/[0.07] bg-white/[0.02] transition-colors hover:border-white/15">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-[14.5px] font-medium text-white">{f.q}</span>
                    <ChevronDown
                      size={17}
                      className={`shrink-0 text-zinc-500 transition-transform duration-200 ${openFAQ === i ? 'rotate-180 text-purple-400' : ''}`}
                    />
                  </button>
                  {openFAQ === i && (
                    <p className="tut-fade border-t border-white/5 px-5 py-4 text-[13.5px] leading-relaxed text-zinc-400">
                      {f.a}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-3xl border border-purple-500/20 bg-gradient-to-b from-purple-950/40 to-transparent px-8 py-14 text-center">
              <h3 className="text-[34px] font-semibold leading-tight tracking-tight text-white sm:text-[42px]">
                That&rsquo;s the whole app.
              </h3>
              <p className="mx-auto mt-4 max-w-md text-[15.5px] leading-relaxed text-zinc-400">
                Open your project and paste your list. You&rsquo;ll have a sequence before you finish your coffee.
              </p>
              <button
                onClick={onComplete}
                className="group mt-8 inline-flex items-center gap-2.5 rounded-xl bg-white px-8 py-4 text-[16px] font-semibold text-black transition-transform hover:scale-[1.02]"
              >
                Open my project
                <ChevronsRight size={19} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        )}

        {/* ---------------- footer nav ---------------- */}
        {chapter !== 'answers' && (
          <div className="mt-16 flex items-center justify-between border-t border-white/5 pt-6">
            {idx > 0 ? (
              <button
                onClick={prev}
                className="flex items-center gap-2 text-[13px] font-medium text-zinc-500 transition-colors hover:text-zinc-200"
              >
                <ArrowLeft size={15} /> {CHAPTERS[idx - 1].label}
              </button>
            ) : (
              <span />
            )}
            <button
              onClick={next}
              className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-2.5 text-[13.5px] font-semibold text-white transition-colors hover:border-white/25 hover:bg-white/[0.08]"
            >
              {CHAPTERS[idx + 1].label}
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default TutorialPage;